import React, { FC, useMemo } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { ReportResultsCont, ResultTableTitle } from './ReportResults.styles';
import CategoriesAndTopicsResults from '../../../components/CategoriesAndTopics/CategoriesAndTopicsResults';
import KpiResults from '../../../components/KpiResults/KpiResults';
import ReportPredictiveStatesTable from '../../../components/ReportPredictiveStatesTable/ReportPredictiveStatesTable';
import TopAndBottomTopics from '../../../components/TopAndBottomTopics/TopAndBottomTopics';
import TopicLevelWorkEnvironment from '../../../components/TopicLevelWorkEnvironment/TopicLevelWorkEnvironment';
import { useReportContext } from '../../../hooks/useReport';

interface IReportResults {
  reportType: string;
}

const ReportResults: FC<IReportResults> = ({ reportType }) => {
  const { t } = useTranslation();

  const { data } = useReportContext();

  const { kpiAnalysisResults, predictiveStatesResults, workEnvironment } = data?.results ?? {};
  const { categoryResults, description, topAndBottomTopics, strengthsAndWeaknesses } =
    workEnvironment ?? {};

  const isExecutiveReport = reportType === 'executive';

  const renderPredictiveStates = useMemo(
    () =>
      !_.isEmpty(predictiveStatesResults) && (
        <ReportPredictiveStatesTable
          description={predictiveStatesResults?.description}
          positiveStates={predictiveStatesResults?.positiveStates}
          negativeStates={predictiveStatesResults?.negativeStates}
          isExecutiveReport={isExecutiveReport}
          sectionTitle={
            <ResultTableTitle id="ResultTableTitle">
              {t('report.predictiveStates.predictiveStates')}
            </ResultTableTitle>
          }
        />
      ),
    [predictiveStatesResults, isExecutiveReport]
  );

  const renderCategoriesAndTopics = useMemo(
    () =>
      !_.isEmpty(categoryResults) &&
      description && (
        <CategoriesAndTopicsResults
          items={categoryResults || []}
          isExecutiveReport={isExecutiveReport}
          description={description}
          sectionTitle={
            <ResultTableTitle id="ResultTableTitle">
              {t('report.results.categoriesAndTopics')}
            </ResultTableTitle>
          }
        />
      ),
    [description, categoryResults, isExecutiveReport, t]
  );

  const renderTopAndBottomTopics = useMemo(
    () =>
      categoryResults && (
        <TopAndBottomTopics
          sectionTitle={
            <ResultTableTitle id="ResultTableTitle">
              {t('report.results.topAndBottomTopics')}
            </ResultTableTitle>
          }
          strengthsAndWeaknesses={strengthsAndWeaknesses}
          topAndBottomTopics={topAndBottomTopics}
          isExecutiveReport={isExecutiveReport}
        />
      ),
    [categoryResults, strengthsAndWeaknesses, topAndBottomTopics, isExecutiveReport, t]
  );

  const renderTopicLevel = useMemo(
    () =>
      categoryResults &&
      description && (
        <TopicLevelWorkEnvironment
          sectionTitle={
            <ResultTableTitle id="ResultTableTitle">
              {t('report.results.topicLevel')}
            </ResultTableTitle>
          }
          data={categoryResults}
          isExecutiveReport={isExecutiveReport}
          description={description}
        />
      ),
    [categoryResults]
  );

  return (
    <ReportResultsCont>
      {/* {informationMessage && <NotificationCont><Notification data={notification} isExpandable/></NotificationCont>} */}
      {kpiAnalysisResults && <KpiResults kpiAnalysisResults={kpiAnalysisResults} />}
      {renderPredictiveStates}
      {renderCategoriesAndTopics}
      {renderTopicLevel}
      {renderTopAndBottomTopics}
    </ReportResultsCont>
  );
};

export default ReportResults;
